/** @format */

import React from "react";
import { Box } from "./MuiCompat";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;
const fontMono = `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;

const card = {
  position: "relative" as const,
  borderRadius: "20px",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  background:
    "linear-gradient(180deg, rgba(17, 18, 20, 0.55) 0%, rgba(7, 8, 10, 0.65) 100%)",
  backdropFilter: "blur(14px)",
  boxShadow:
    "0 24px 60px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  overflow: "hidden",
  padding: { xs: "20px", md: "26px" },
};

const microLabel = {
  fontFamily: fontMono,
  fontSize: "10.5px",
  fontWeight: 500,
  letterSpacing: "0.18em",
  textTransform: "uppercase" as const,
  color: "rgba(255, 255, 255, 0.45)",
};

const tagPill = {
  display: "inline-flex",
  alignItems: "center",
  px: "9px",
  py: "4px",
  borderRadius: "6px",
  backgroundColor: "rgba(11, 13, 16, 0.75)",
  border: "1px solid rgba(255, 255, 255, 0.06)",
  fontFamily: fontMono,
  fontSize: "10.5px",
  fontWeight: 500,
  letterSpacing: "0.04em",
  color: "rgba(255, 255, 255, 0.7)",
};

// =====================================================================
// Card 1 — Weight loss · Ranked factors
// =====================================================================
const WeightLossIllustration: React.FC = () => {
  const factors = [
    { label: "Sleep", value: 38, top: true },
    { label: "Glucose", value: 24 },
    { label: "Late meals", value: 18 },
    { label: "Protein", value: 12 },
    { label: "Caffeine", value: 8 },
  ];
  return (
    <Box sx={card}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={microLabel}>Weight loss · Top factors</Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            ...microLabel,
            color: "rgba(86, 194, 255, 0.7)",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#56C2FF",
              boxShadow: "0 0 10px rgba(86, 194, 255, 0.7)",
              animation: "wlPulse 1.6s ease-in-out infinite",
              "@keyframes wlPulse": {
                "0%, 100%": { opacity: 0.5 },
                "50%": { opacity: 1 },
              },
            }}
          />
          live
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {factors.map((f, i) => (
          <Box
            key={f.label}
            sx={{
              display: "grid",
              gridTemplateColumns: "100px 1fr 44px",
              alignItems: "center",
              gap: "16px",
              opacity: 0,
              animation: `wlBarReveal 0.7s cubic-bezier(0.23, 1, 0.32, 1) ${
                0.1 + i * 0.08
              }s forwards`,
              "@keyframes wlBarReveal": {
                from: { opacity: 0, transform: "translateY(6px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Box
              sx={{
                fontFamily: fontInter,
                fontSize: "13px",
                fontWeight: 500,
                color: f.top
                  ? "var(--color-snow)"
                  : "rgba(255, 255, 255, 0.62)",
              }}
            >
              {f.label}
            </Box>
            <Box
              sx={{
                position: "relative",
                height: 6,
                borderRadius: "999px",
                backgroundColor: "rgba(255, 255, 255, 0.04)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${f.value}%`,
                  borderRadius: "999px",
                  background: f.top
                    ? "linear-gradient(90deg, rgba(86, 194, 255, 0.65), rgba(86, 194, 255, 1))"
                    : "linear-gradient(90deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.42))",
                  boxShadow: f.top
                    ? "0 0 14px rgba(86, 194, 255, 0.55)"
                    : "none",
                  animation: f.top
                    ? "wlTopGlow 2.6s ease-in-out infinite"
                    : "none",
                  "@keyframes wlTopGlow": {
                    "0%, 100%": {
                      boxShadow: "0 0 12px rgba(86, 194, 255, 0.4)",
                    },
                    "50%": {
                      boxShadow: "0 0 22px rgba(86, 194, 255, 0.75)",
                    },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                fontFamily: fontMono,
                fontSize: "12px",
                fontWeight: 500,
                color: f.top
                  ? "var(--color-snow)"
                  : "rgba(255, 255, 255, 0.55)",
                textAlign: "right",
              }}
            >
              {f.value}%
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: "16px",
          borderTop: "1px solid rgba(255, 255, 255, 0.06)",
          display: "flex",
          justifyContent: "space-between",
          ...microLabel,
        }}
      >
        <span>agent.13a4 · running</span>
        <span>n=82d · ψ=0.91</span>
      </Box>
    </Box>
  );
};

// =====================================================================
// Card 2 — Cognition forecast
// =====================================================================
const ForecastIllustration: React.FC = () => {
  // Two paths. Current: roughly flat. Predicted: rises.
  const days = [0, 2, 4, 6, 8, 10, 12, 14];
  const current = [50, 51, 49, 50, 50, 49, 50, 50];
  const predicted = [50, 52, 55, 59, 64, 68, 72, 76];

  const xScale = (d: number) => (d / 14) * 100;
  const yScale = (v: number) => 100 - ((v - 40) / 50) * 100;

  const linePath = (values: number[]) =>
    values
      .map((v, i) => `${i === 0 ? "M" : "L"}${xScale(days[i])},${yScale(v)}`)
      .join(" ");

  const areaPath = (values: number[]) =>
    `${linePath(values)} L${xScale(days[days.length - 1])},100 L${xScale(
      0,
    )},100 Z`;

  return (
    <Box sx={card}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={microLabel}>Cognition forecast · 14d</Box>
        <Box
          sx={{
            ...tagPill,
            color: "rgba(86, 194, 255, 0.95)",
            borderColor: "rgba(86, 194, 255, 0.3)",
            backgroundColor: "rgba(4, 63, 150, 0.18)",
          }}
        >
          if: caffeine cutoff = 14:00
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
          }}
        >
          <defs>
            <linearGradient id="forecastFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(86, 194, 255, 0.32)" />
              <stop offset="100%" stopColor="rgba(86, 194, 255, 0)" />
            </linearGradient>
          </defs>

          {[20, 40, 60, 80].map((y) => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="rgba(255, 255, 255, 0.04)"
              strokeWidth="0.3"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* Predicted area */}
          <path d={areaPath(predicted)} fill="url(#forecastFill)" />

          {/* Current line (gray, baseline) */}
          <path
            d={linePath(current)}
            fill="none"
            stroke="rgba(255, 255, 255, 0.32)"
            strokeWidth="0.8"
            strokeDasharray="1.4 1.6"
            vectorEffect="non-scaling-stroke"
          />

          {/* Predicted line */}
          <path
            d={linePath(predicted)}
            fill="none"
            stroke="#56C2FF"
            strokeWidth="1.1"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{
              filter: "drop-shadow(0 0 4px rgba(86, 194, 255, 0.6))",
            }}
          />

          {/* Animated signal trail along predicted line */}
          <path
            d={linePath(predicted)}
            fill="none"
            stroke="rgba(255, 255, 255, 0.95)"
            strokeWidth="1.1"
            strokeLinecap="round"
            strokeDasharray="2 18"
            vectorEffect="non-scaling-stroke"
            style={{
              animation: "forecastFlow 2.4s linear infinite",
            }}
          />

          <style>{`
            @keyframes forecastFlow {
              from { stroke-dashoffset: 0; }
              to { stroke-dashoffset: -20; }
            }
          `}</style>
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: `${xScale(days[days.length - 1])}%`,
            top: `${yScale(predicted[predicted.length - 1])}%`,
            transform: "translate(-50%, -50%)",
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 0 10px rgba(86, 194, 255, 0.9)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "0%",
            right: "0%",
            transform: "translate(0, -100%)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: fontMono,
            fontSize: "11px",
            fontWeight: 500,
            color: "rgba(255, 255, 255, 0.92)",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#56C2FF",
              fontWeight: 600,
            }}
          >
            +52%
          </Box>
          <Box component="span" sx={{ color: "rgba(255,255,255,0.4)" }}>
            cognition · 14d
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          ...microLabel,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <span>day 0</span>
          <span>day 14</span>
        </Box>
        <span>ψ=0.87</span>
      </Box>
    </Box>
  );
};

// =====================================================================
// Card 3 — Cohort knowledge feed
//
// "You" at the center. Three peer clusters in different corners — each
// cluster has discovered a different finding, which floats between the
// cluster and you. The text on the left does the explaining; the visual
// just needs to feel like three streams of learning converging.
// =====================================================================
const CohortIllustration: React.FC = () => {
  type Cluster = {
    peers: Array<{ x: number; y: number }>;
    sourceIdx: number;
    finding: string;
    chip: { x: number; y: number };
  };

  const clusters: Cluster[] = [
    {
      peers: [
        { x: 4, y: 6 },
        { x: 10, y: 12 },
        { x: 16, y: 4 },
        { x: 18, y: 12 }, // ← source
        { x: 22, y: 6 },
        { x: 8, y: 18 },
        { x: 16, y: 20 },
        { x: 24, y: 16 },
        { x: 28, y: 22 },
        { x: 12, y: 26 },
      ],
      sourceIdx: 3,
      finding: "magnesium · 90m pre-bed",
      chip: { x: 30, y: 36 },
    },
    {
      peers: [
        { x: 96, y: 6 },
        { x: 90, y: 12 },
        { x: 84, y: 4 },
        { x: 82, y: 12 }, // ← source
        { x: 78, y: 6 },
        { x: 92, y: 18 },
        { x: 84, y: 20 },
        { x: 76, y: 16 },
        { x: 72, y: 22 },
        { x: 88, y: 26 },
      ],
      sourceIdx: 3,
      finding: "cool room · < 19°C",
      chip: { x: 70, y: 36 },
    },
    {
      peers: [
        { x: 32, y: 90 },
        { x: 38, y: 84 },
        { x: 44, y: 92 },
        { x: 50, y: 86 }, // ← source
        { x: 50, y: 96 },
        { x: 56, y: 92 },
        { x: 62, y: 84 },
        { x: 68, y: 90 },
        { x: 40, y: 96 },
        { x: 60, y: 96 },
      ],
      sourceIdx: 3,
      finding: "11pm screen cutoff",
      chip: { x: 50, y: 76 },
    },
  ];

  const center = { x: 50, y: 50 };

  // Ambient population — dim dots scattered across the empty regions of the
  // canvas. These represent the broader cohort underneath; the named
  // clusters above are the subset selected for you.
  const ambientPeers: Array<{ x: number; y: number }> = [
    // top-middle band, between the two top clusters
    { x: 36, y: 8 },
    { x: 44, y: 4 },
    { x: 52, y: 16 },
    { x: 60, y: 12 },
    { x: 64, y: 4 },
    { x: 40, y: 24 },
    { x: 56, y: 26 },
    // middle-left
    { x: 4, y: 36 },
    { x: 12, y: 44 },
    { x: 4, y: 52 },
    { x: 14, y: 58 },
    { x: 6, y: 66 },
    { x: 18, y: 68 },
    { x: 8, y: 76 },
    { x: 22, y: 52 },
    // middle-right
    { x: 96, y: 36 },
    { x: 88, y: 44 },
    { x: 96, y: 52 },
    { x: 86, y: 58 },
    { x: 94, y: 66 },
    { x: 82, y: 68 },
    { x: 92, y: 76 },
    { x: 78, y: 52 },
    // bottom-left flank
    { x: 6, y: 84 },
    { x: 18, y: 90 },
    { x: 4, y: 96 },
    { x: 24, y: 80 },
    // bottom-right flank
    { x: 94, y: 84 },
    { x: 82, y: 90 },
    { x: 96, y: 96 },
    { x: 76, y: 80 },
  ];

  // Even-finer background layer — smaller, dimmer dots that fill the gaps
  // between the ambient peers above. Adds depth without adding noise.
  const ambientFinePeers: Array<{ x: number; y: number }> = [
    // top-middle gaps
    { x: 32, y: 14 },
    { x: 48, y: 8 },
    { x: 38, y: 16 },
    { x: 54, y: 22 },
    { x: 62, y: 18 },
    { x: 44, y: 14 },
    // middle-left gaps
    { x: 10, y: 38 },
    { x: 16, y: 50 },
    { x: 4, y: 44 },
    { x: 12, y: 62 },
    { x: 24, y: 64 },
    { x: 4, y: 72 },
    { x: 20, y: 76 },
    // middle-right gaps
    { x: 90, y: 38 },
    { x: 84, y: 50 },
    { x: 96, y: 44 },
    { x: 88, y: 62 },
    { x: 76, y: 64 },
    { x: 96, y: 72 },
    { x: 80, y: 76 },
    // bottom flanks
    { x: 10, y: 88 },
    { x: 28, y: 86 },
    { x: 14, y: 80 },
    { x: 90, y: 88 },
    { x: 72, y: 86 },
    { x: 86, y: 80 },
  ];

  // Curve a path from a → b with a small perpendicular bow so lines don't
  // sit dead-straight (visually more alive).
  const curve = (
    a: { x: number; y: number },
    b: { x: number; y: number },
    bow = 4,
  ) => {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = (-dy / len) * bow;
    const ny = (dx / len) * bow;
    return `M${a.x},${a.y} Q${mx + nx},${my + ny} ${b.x},${b.y}`;
  };

  return (
    <Box sx={card}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={microLabel}>Cohort · sleep_quality</Box>
        <Box sx={microLabel}>n=4,217</Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 0.95",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
          }}
        >
          {/* For each cluster: source peer → chip → you */}
          {clusters.map((c, i) => {
            const src = c.peers[c.sourceIdx];
            return (
              <g key={`flow-${i}`}>
                <path
                  d={curve(src, c.chip, 3)}
                  fill="none"
                  stroke="rgba(86,194,255,0.55)"
                  strokeWidth="0.32"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={curve(c.chip, center, 3)}
                  fill="none"
                  stroke="rgba(86,194,255,0.5)"
                  strokeWidth="0.3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}

          {/* Animated cyan dash on the first cluster's chip→you segment */}
          <path
            d={curve(clusters[0].chip, center, 3)}
            fill="none"
            stroke="#56C2FF"
            strokeWidth="0.6"
            strokeLinecap="round"
            strokeDasharray="2 30"
            vectorEffect="non-scaling-stroke"
            style={{
              animation: "cohortFlow 2.4s linear infinite",
              filter: "drop-shadow(0 0 3px rgba(86,194,255,0.9))",
            }}
          />

          <style>{`
            @keyframes cohortFlow {
              from { stroke-dashoffset: 0; }
              to { stroke-dashoffset: -32; }
            }
          `}</style>
        </Box>

        {/* Ambient population — the broader cohort underneath. Paints on top
            of cluster halos but below the named cluster dots so the named
            clusters read as a selected subset of a larger population. */}
        {ambientPeers.map((p, i) => (
          <Box
            key={`ambient-${i}`}
            sx={{
              position: "absolute",
              left: `${p.x}%`,
              top: `${p.y}%`,
              transform: "translate(-50%, -50%)",
              width: 5.5,
              height: 5.5,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.22)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ))}

        {/* Even finer background layer — smaller, dimmer dots filling the
            gaps between the ambient layer for added depth. */}
        {ambientFinePeers.map((p, i) => (
          <Box
            key={`ambient-fine-${i}`}
            sx={{
              position: "absolute",
              left: `${p.x}%`,
              top: `${p.y}%`,
              transform: "translate(-50%, -50%)",
              width: 3,
              height: 3,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.14)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ))}

        {/* Soft cluster halos — make each constellation read as a region of
            activity rather than scattered dots. */}
        {clusters.map((c, ci) => {
          const cx = c.peers.reduce((s, p) => s + p.x, 0) / c.peers.length;
          const cy = c.peers.reduce((s, p) => s + p.y, 0) / c.peers.length;
          return (
            <Box
              key={`halo-${ci}`}
              sx={{
                position: "absolute",
                left: `${cx}%`,
                top: `${cy}%`,
                transform: "translate(-50%, -50%)",
                width: 150,
                height: 150,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(86,194,255,0.16) 0%, rgba(86,194,255,0) 65%)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
          );
        })}

        {/* Peer dots — one per cluster is lit cyan as the source */}
        {clusters.flatMap((c, ci) =>
          c.peers.map((p, pi) => {
            const isSource = pi === c.sourceIdx;
            return (
              <Box
                key={`peer-${ci}-${pi}`}
                sx={{
                  position: "absolute",
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  transform: "translate(-50%, -50%)",
                  width: isSource ? 9 : 5.5,
                  height: isSource ? 9 : 5.5,
                  borderRadius: "50%",
                  backgroundColor: isSource
                    ? "#56C2FF"
                    : "rgba(255,255,255,0.7)",
                  boxShadow: isSource
                    ? "0 0 14px rgba(86,194,255,0.9)"
                    : "0 0 4px rgba(255,255,255,0.25)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            );
          }),
        )}

        {/* Finding chips — one per cluster, sitting between cluster and you */}
        {clusters.map((c, i) => (
          <Box
            key={`chip-${i}`}
            sx={{
              position: "absolute",
              left: `${c.chip.x}%`,
              top: `${c.chip.y}%`,
              transform: "translate(-50%, -50%)",
              px: "11px",
              py: "6px",
              borderRadius: "8px",
              backgroundColor: "rgba(11, 12, 14, 0.92)",
              border: "1px solid rgba(86,194,255,0.2)",
              boxShadow:
                "0 6px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
              fontFamily: fontMono,
              fontSize: { xs: "10.5px", md: "11.5px" },
              fontWeight: 500,
              color: "rgba(255,255,255,0.88)",
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
              zIndex: 3,
            }}
          >
            {c.finding}
          </Box>
        ))}

        {/* "you" at the center */}
        <Box
          sx={{
            position: "absolute",
            left: `${center.x}%`,
            top: `${center.y}%`,
            transform: "translate(-50%, -50%)",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(86,194,255,0.5) 0%, rgba(86,194,255,0) 70%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: `${center.x}%`,
            top: `${center.y}%`,
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            filter: "drop-shadow(0 0 10px rgba(255,255,255,0.55))",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <PersonOutlinedIcon sx={{ fontSize: 34 }} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: `${center.x}%`,
            top: `${center.y}%`,
            transform: "translate(-50%, calc(-50% + 28px))",
            fontFamily: fontInter,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: "var(--color-snow)",
            zIndex: 2,
          }}
        >
          you
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
// Card 4 — Coach handoff
// =====================================================================
const CoachIllustration: React.FC = () => {
  const sparkPath =
    "M0,32 L10,28 L20,30 L30,22 L40,24 L50,18 L60,20 L70,12 L80,14 L90,8 L100,10";

  const panelBase = {
    position: "relative" as const,
    borderRadius: "12px",
    padding: "16px",
    minHeight: 168,
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  };

  return (
    <Box sx={card}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={microLabel}>Coach sync · live</Box>
        <Box sx={{ ...microLabel, color: "rgba(255, 255, 255, 0.6)" }}>
          consent · verified
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 22px 1fr",
          gap: "8px",
          alignItems: "stretch",
        }}
      >
        {/* You panel */}
        <Box
          sx={{
            ...panelBase,
            border: "1px solid rgba(255, 255, 255, 0.07)",
            backgroundColor: "rgba(7, 9, 12, 0.85)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              ...microLabel,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #56C2FF, #523091)",
              }}
            />
            <span style={{ color: "rgba(255,255,255,0.82)" }}>you</span>
          </Box>
          <Box
            component="svg"
            viewBox="0 0 100 36"
            preserveAspectRatio="none"
            sx={{ width: "100%", height: 52, display: "block" }}
          >
            <path
              d={sparkPath}
              fill="none"
              stroke="rgba(255, 255, 255, 0.55)"
              strokeWidth="1"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              ...microLabel,
              color: "rgba(255, 255, 255, 0.55)",
            }}
          >
            <span>sleep · 7.2h</span>
            <span>recovery · 64</span>
          </Box>
        </Box>

        {/* Connector — a narrow gate with 3 staggered dots flowing left → right
            along a thin track. overflow: hidden clips any escaping glow so it
            can't bleed vertically into the rest of the card. */}
        <Box
          sx={{
            position: "relative",
            width: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: 1,
              transform: "translateY(-50%)",
              background:
                "linear-gradient(90deg, rgba(86, 194, 255, 0), rgba(86, 194, 255, 0.45) 35%, rgba(86, 194, 255, 0.45) 65%, rgba(86, 194, 255, 0) 100%)",
            }}
          /> */}
          {[0, 0.8, 1.6].map((delay, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "50%",
                left: 0,
                width: 3,
                height: 3,
                borderRadius: "50%",
                backgroundColor: "#56C2FF",
                boxShadow: "0 0 4px rgba(86, 194, 255, 0.65)",
                animation: `coachFlow 2.4s linear ${delay}s infinite`,
                "@keyframes coachFlow": {
                  from: { transform: "translate(-4px, -50%)", opacity: 0 },
                  "15%": { opacity: 1 },
                  "85%": { opacity: 1 },
                  to: { transform: "translate(22px, -50%)", opacity: 0 },
                },
              }}
            />
          ))}
        </Box>

        {/* Coach panel */}
        <Box
          sx={{
            ...panelBase,
            border: "1px solid rgba(86, 194, 255, 0.22)",
            background:
              "linear-gradient(180deg, rgba(4, 63, 150, 0.18) 0%, rgba(7, 9, 12, 0.85) 100%)",
            boxShadow: "0 0 24px rgba(86, 194, 255, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              ...microLabel,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "rgba(86, 194, 255, 0.18)",
                border: "1px solid rgba(86, 194, 255, 0.55)",
              }}
            />
            <span style={{ color: "rgba(86, 194, 255, 0.95)" }}>coach</span>
          </Box>
          <Box sx={{ position: "relative", width: "100%", height: 52 }}>
            <Box
              component="svg"
              viewBox="0 0 100 36"
              preserveAspectRatio="none"
              sx={{ width: "100%", height: 52, display: "block" }}
            >
              <path
                d={sparkPath}
                fill="none"
                stroke="#56C2FF"
                strokeWidth="1.2"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                style={{
                  filter: "drop-shadow(0 0 4px rgba(86, 194, 255, 0.55))",
                }}
              />
              <line
                x1="60"
                y1="20"
                x2="60"
                y2="6"
                stroke="rgba(86, 194, 255, 0.45)"
                strokeWidth="0.4"
                strokeDasharray="1 1"
                vectorEffect="non-scaling-stroke"
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                left: "60%",
                top: `${(20 / 36) * 100}%`,
                transform: "translate(-50%, -50%)",
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 0 5px rgba(86, 194, 255, 0.65)",
                pointerEvents: "none",
              }}
            />
          </Box>
          <Box
            sx={{
              fontFamily: fontInter,
              fontSize: "11.5px",
              lineHeight: 1.4,
              color: "rgba(255, 255, 255, 0.82)",
            }}
          >
            ↑ Bumped sleep target to 8h
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: "14px",
          borderTop: "1px solid rgba(255, 255, 255, 0.06)",
          display: "flex",
          justifyContent: "space-between",
          ...microLabel,
        }}
      >
        <span>EON · sync</span>
        <span>last · 4 min ago</span>
      </Box>
    </Box>
  );
};

// =====================================================================
// Row layout
// =====================================================================
interface CapabilityRow {
  number: string;
  title: React.ReactNode;
  description: string;
  tags?: string[];
  illustration: React.ReactNode;
}

const rows: CapabilityRow[] = [
  {
    number: "01",
    title: "Discover what matters most for your goals",
    description:
      "Your agent runs experiments across nutrition, sleep, and supplements to discover what actually moves the needle for your weight loss.",
    tags: ["Nutrition", "Sleep", "Supplements"],
    illustration: <WeightLossIllustration />,
  },
  {
    number: "02",
    title: <>Forecast how your habits improve your outcomes</>,
    description:
      "See how cutting caffeine after 2pm, or adding magnesium, would shift your focus and sleep. Before you commit.",
    illustration: <ForecastIllustration />,
  },
  {
    number: "03",
    title: "Learn from people like you",
    description:
      "Your agent draws on what actually worked for thousands of people with goals like yours. Privately, without your data leaving you.",
    illustration: <CohortIllustration />,
  },
  {
    number: "04",
    title: "Connect your data and agents with people you trust",
    description:
      "Share what your agent learns with the people guiding you. Automatically, with your consent, in the form they need.",
    illustration: <CoachIllustration />,
  },
];

const CapabilityRowComponent: React.FC<{
  row: CapabilityRow;
  index: number;
}> = ({ row, index }) => {
  const reverse = index % 2 === 1;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: { md: 10, lg: 12 },
        rowGap: { xs: 4, md: 0 },
        alignItems: "center",
        py: { xs: 7, md: 11 },
      }}
    >
      <Box
        sx={{
          order: { xs: 1, md: reverse ? 2 : 1 },
          maxWidth: 480,
        }}
      >
        <Box sx={{ ...microLabel, mb: 2 }}>{row.number} / 04</Box>
        <Box
          component="h3"
          sx={{
            m: 0,
            fontFamily: fontInter,
            fontWeight: 600,
            fontSize: "clamp(1.6rem, 2.8vw, 2.2rem)",
            lineHeight: 1.18,
            letterSpacing: "-0.012em",
            color: "var(--color-snow)",
          }}
        >
          {row.title}
        </Box>
        <Box
          sx={{
            mt: 2.5,
            fontFamily: fontInter,
            fontSize: "16px",
            lineHeight: 1.55,
            color: "rgba(255, 255, 255, 0.62)",
          }}
        >
          {row.description}
        </Box>
        {row.tags && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {row.tags.map((tag) => (
              <Box key={tag} sx={tagPill}>
                {tag}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ order: { xs: 2, md: reverse ? 1 : 2 } }}>
        {row.illustration}
      </Box>
    </Box>
  );
};

const Capabilities: React.FC = () => {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 10, md: 14 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "var(--page-max-width)",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 8 },
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            maxWidth: "780px",
            fontFamily: fontInter,
            fontWeight: 600,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: "var(--leading-heading)",
            letterSpacing: "var(--tracking-heading-lg)",
            color: "var(--color-snow)",
          }}
        >
          AI agents do the work in the background.
        </Box>
        <Box
          sx={{
            mt: 2,
            maxWidth: "640px",
            fontFamily: fontInter,
            fontSize: "var(--text-subheading)",
            lineHeight: "var(--leading-subheading)",
            letterSpacing: "var(--tracking-subheading)",
            color: "var(--color-slate-200)",
          }}
        >
          Your goals progress while you live your life.
        </Box>

        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            "& > * + *": {
              borderTop: "1px solid rgba(255, 255, 255, 0.06)",
            },
          }}
        >
          {rows.map((row, i) => (
            <CapabilityRowComponent key={row.number} row={row} index={i} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Capabilities;
